import styles from './index.module.scss';
import React, { MouseEventHandler, useState, useEffect, useRef } from 'react';
import { ErrorMessageModel } from '../../api/models';
import { RspConfirmationModel } from '../../api/models/rsp/RspConfirmationModel';
import { PrimaryButton, TertiaryButton } from '../../components';
import { Form } from '../../components/form/pageForm';
import { FormReference, PageFormModel } from '../../components/form/pageForm/types';
import { ErrorMessage } from '../../components/models';
import applicationFinancialYear from '../../FormDefinitions/RspVariation/applicationFinancialYear';
import { usePostApiData } from '../../hooks/usePostApiData';
import { Redirect } from 'react-router';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { extractHardValidations } from '../../validation/util';
import { mapErrors } from './errorMapping';

const { modalApplication, modalApplicationBody, modalApplicationButtonGroup, editModalButton, cancelModalButton, spinnerBox } = styles;

interface BeginRspVariationModalProps {
    onClickCancel: MouseEventHandler<HTMLButtonElement>;
    beginRspModel: Partial<RspConfirmationModel>;
    onModalToggle?: () => void;
    modalTitle: string;
    showModal: boolean;
    apiEndpoint: string;
}

interface FormDataState<T> {
    pageName: string;
    formData: Partial<T>;
}

export const BeginRspVariationModal = (props: BeginRspVariationModalProps) => {
    const { onClickCancel, onModalToggle, beginRspModel, modalTitle, showModal, apiEndpoint } = props;
    const pageName = 'applicationFinancialYear';
    const [formDataState, setFormData] = useState<FormDataState<RspConfirmationModel>>(
        {
            pageName,
            formData: beginRspModel,
        },
    );

    const [apiErrors, setApiErrors] = useState<Partial<ErrorMessageModel>[] | undefined>(undefined);
    const [validationErrors, setValidationErrors] = useState<Partial<ErrorMessage>[] | undefined>(undefined);
    const [shouldSubmitApplication, setShouldSubmitApplication] = useState<boolean>(false);
    const { formData } = formDataState;
    const [createRspState, createRsp] =
        usePostApiData<Partial<RspConfirmationModel>, number>({ apiEndpoint, data: {} });

    const [isCreatingApplication, setIsCreatingApplication] = useState(false);

    const { response: rspRequestId,
            isErrorCalling: isErrorCallingBeginApplication,
            errors: applicationCreationApiErrors,
            validations: applicationCreationValidationErrors,
    } = createRspState;

    const onClickBeginRsp = () => {
        setShouldSubmitApplication(true);
        formRef.current !== null && formRef.current.submitForm();
    };

    const handleSubmit = (values: Partial<RspConfirmationModel>) => {
        setFormData(s => ({
            ...s,
            formData: { ...values },
            errorsFromLoad: false,
        }),
        );
    };

    useEffect(() => {
        if (formData.financialYear !== '' && shouldSubmitApplication) {
            createRsp(formData);
            setIsCreatingApplication(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    useEffect(() => {
        setApiErrors((isErrorCallingBeginApplication) ? applicationCreationApiErrors : undefined);
        if (isErrorCallingBeginApplication) {
            setIsCreatingApplication(false);
        }
    }, [isErrorCallingBeginApplication, applicationCreationApiErrors]);

    useEffect(() => {
        setValidationErrors(isErrorCallingBeginApplication ? extractHardValidations(applicationCreationValidationErrors) : undefined);
        if (isErrorCallingBeginApplication) {
            setIsCreatingApplication(false);
        }
    }, [isErrorCallingBeginApplication, applicationCreationValidationErrors]);

    useEffect(() => {
        setShouldSubmitApplication(false);
        setApiErrors(undefined);
        setValidationErrors(undefined);
        setIsCreatingApplication(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    const formRef = useRef<FormReference>(null);

    const formProps: PageFormModel<RspConfirmationModel> = {
        fields: applicationFinancialYear,
        data: formData,
        handleSubmit,
        formName: 'RspVariation',
        name: pageName,
        apiErrors: { errors: mapErrors(apiErrors), actionType: undefined },
        saveErrors: validationErrors,
        initialPageValues: beginRspModel,
        isModalForm: true,
        showAllValidationErrors: true,
        validations: undefined,
        errorsFromLoad: false,
        formRef,
        submitValidations: applicationCreationValidationErrors ? applicationCreationValidationErrors : [],
    };

    useEffect(() => {
        setFormData(s => ({ ...s, formData: beginRspModel }));
    }, [beginRspModel]);

    if (rspRequestId && Number(rspRequestId) > 0) {
        return <Redirect to={`/application/rspvariation/${rspRequestId}`} />;
    }

    return (
        <Modal
            backdrop={'static'}
            isOpen={showModal}
            toggle={onModalToggle}
            contentClassName={modalApplication}
            returnFocusAfterClose={true}
            size='lg'
            scrollable={true}
            data-testid='beginRspVariationModal'
            labelledBy='beginRspVariationModalTitle'
        >
            <ModalBody className={modalApplicationBody}>
                <div>
                    <h2 id='beginRspVariationModalTitle'>{modalTitle}</h2>
                </div>
                <hr aria-hidden='true' />
                {isCreatingApplication ? <div className={spinnerBox}><Spinner /></div> : <Form {...formProps} />}
                <div className={modalApplicationButtonGroup}>
                    <TertiaryButton
                        id={`cancelRspVariationApplication`}
                        className={`${editModalButton} ${cancelModalButton}`}
                        disabled={isCreatingApplication}
                        onClick={onClickCancel}
                    >
                        Cancel
                    </TertiaryButton>
                    <PrimaryButton
                        id={`beginRspVariationApplication`}
                        className={editModalButton}
                        disabled={isCreatingApplication}
                        onClick={onClickBeginRsp}
                    >
                        Begin application
                    </PrimaryButton>
                </div>
            </ModalBody>
        </Modal>
    );
};
